import React, { useState, useMemo } from "react";
import { useUserContext } from "../../../context/UserContext";
import { collaborationApi } from "../../../services/api/collaborationApi";
import { toast } from "react-toastify";
import {
  FiUsers,
  FiBriefcase,
  FiClock,
  FiCalendar,
  FiCheckCircle,
  FiX,
  FiAlertCircle,
  FiSend,
  FiSettings,
  FiXCircle,
  FiMapPin,
} from "react-icons/fi";
import ApplyModal from "../ApplyModal";
import ManageApplicationsModal from "../ManageApplicationsModal";
import ConfirmModal from "../ConfirmModal";
import "./CollaborationPost.css";

const CollaborationPost = ({ post, onUpdate }) => {
  const { user } = useUserContext();
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closing, setClosing] = useState(false);

  const collab = post.collaboration;

  // Get current user ID (backend uses 'id', not '_id')
  const currentUserId = user?.id || user?._id || user?.userId;
  const isAuthor = String(post.author._id) === String(currentUserId);

  // Calculate positions
  const totalPositions = useMemo(
    () => collab.positions.reduce((sum, pos) => sum + pos.count, 0),
    [collab.positions]
  );

  const filledPositions = useMemo(
    () => collab.positions.reduce((sum, pos) => sum + (pos.filled || 0), 0),
    [collab.positions]
  );

  const availablePositions = totalPositions - filledPositions;

  // Check if user already applied
  const userApplication = useMemo(
    () =>
      collab.applicants?.find(
        (app) => String(app.user._id || app.user) === String(currentUserId)
      ),
    [collab.applicants, currentUserId]
  );

  // Check if deadline passed
  const isPastDeadline = useMemo(() => {
    if (!collab.deadline) return false;
    return new Date(collab.deadline) < new Date();
  }, [collab.deadline]);

  // Check if can apply
  const canApply =
    user &&
    !isAuthor &&
    !userApplication &&
    collab.isOpen &&
    !isPastDeadline &&
    availablePositions > 0;

  // Handle apply
  const handleApply = async (motivation, positionIndex) => {
    try {
      const result = await collaborationApi.applyToCollaboration(post._id, {
        motivation,
        positionIndex,
      });

      if (result.data.success) {
        toast.success("Application submitted successfully!");
        setShowApplyModal(false);

        // Update post with new application
        if (onUpdate) {
          onUpdate(post._id, {
            collaboration: {
              ...collab,
              applicants: [
                ...(collab.applicants || []),
                result.data.application,
              ],
            },
          });
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to apply");
    }
  };

  // Handle close collaboration
  const handleClose = async () => {
    setClosing(true);
    try {
      const result = await collaborationApi.closeCollaboration(post._id);

      if (result.data.success) {
        toast.success("Collaboration closed successfully");
        setShowCloseModal(false);

        // Update post with closed status
        if (onUpdate) {
          onUpdate(post._id, {
            collaboration: {
              ...collab,
              isOpen: false,
            },
          });
        }
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to close collaboration"
      );
    } finally {
      setClosing(false);
    }
  };

  return (
    <div className="cbx-cp">
      {/* Header */}
      <div className="cbx-cp__header">
        <div className="cbx-cp__title-row">
          <h2 className="cbx-cp__title">{collab.title}</h2>
          {!collab.isOpen && (
            <span className="cbx-cp__closed">
              <FiX /> Closed
            </span>
          )}
        </div>

        {/* Progress */}
        <div className="cbx-cp__progress">
          <div className="cbx-cp__progress-info">
            <FiUsers className="cbx-cp__progress-icon" />
            <span>{filledPositions}/{totalPositions} Positions Filled</span>
          </div>
          <div className="cbx-cp__progress-bar">
            <div
              className="cbx-cp__progress-fill"
              style={{ width: `${(filledPositions / totalPositions) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="cbx-cp__description">
        <p>{collab.description}</p>
      </div>

      {/* Additional Content */}
      {post.content && (
        <div className="cbx-cp__extra">
          <h4>Additional Information</h4>
          <p>{post.content}</p>
        </div>
      )}

      {/* Positions */}
      <div className="cbx-cp__positions">
        <h4 className="cbx-cp__section-title">Open Positions</h4>
        <div className="cbx-cp__positions-list">
          {collab.positions.map((position, index) => (
            <div key={index} className="cbx-cp__position">
              <FiMapPin className="cbx-cp__position-icon" />
              <span className="cbx-cp__position-role">{position.role}</span>
              <span className="cbx-cp__position-count">
                {position.filled || 0}/{position.count}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Required Skills */}
      {collab.requiredSkills && collab.requiredSkills.length > 0 && (
        <div className="cbx-cp__skills">
          <h4 className="cbx-cp__section-title">Required Skills</h4>
          <div className="cbx-cp__skills-list">
            {collab.requiredSkills.map((skill, index) => (
              <span key={index} className="cbx-cp__skill">{skill}</span>
            ))}
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="cbx-cp__meta">
        <div className="cbx-cp__meta-item">
          <FiClock className="cbx-cp__meta-icon" />
          <div>
            <span className="cbx-cp__meta-label">Duration</span>
            <span className="cbx-cp__meta-value">
              {collab.duration === "short-term"
                ? "Short-term"
                : collab.duration === "long-term"
                ? "Long-term"
                : "Ongoing"}
            </span>
          </div>
        </div>

        <div className="cbx-cp__meta-item">
          <FiBriefcase className="cbx-cp__meta-icon" />
          <div>
            <span className="cbx-cp__meta-label">Type</span>
            <span className="cbx-cp__meta-value">
              {collab.collaborationType === "paper-writing"
                ? "Paper Writing"
                : collab.collaborationType === "data-analysis"
                ? "Data Analysis"
                : collab.collaborationType === "experiment"
                ? "Experiment"
                : collab.collaborationType === "review"
                ? "Review"
                : collab.collaborationType === "research"
                ? "Research"
                : "Other"}
            </span>
          </div>
        </div>

        {collab.deadline && (
          <div className="cbx-cp__meta-item">
            <FiCalendar className="cbx-cp__meta-icon" />
            <div>
              <span className="cbx-cp__meta-label">Deadline</span>
              <span className={`cbx-cp__meta-value ${isPastDeadline ? "cbx-cp__meta-value--past" : ""}`}>
                {new Date(collab.deadline).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}

        {collab.researchRoom && (
          <div className="cbx-cp__meta-item">
            <FiUsers className="cbx-cp__meta-icon" />
            <div>
              <span className="cbx-cp__meta-label">Room</span>
              <span className="cbx-cp__meta-value cbx-cp__meta-value--link">
                {collab.researchRoom.name || collab.researchRoom}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Application Status */}
      {userApplication && (
        <div className={`cbx-cp__status cbx-cp__status--${userApplication.status}`}>
          {userApplication.status === "pending" && (
            <>
              <FiAlertCircle className="cbx-cp__status-icon" />
              <span>Your application is pending review</span>
            </>
          )}
          {userApplication.status === "accepted" && (
            <>
              <FiCheckCircle className="cbx-cp__status-icon" />
              <span>Your application was accepted!</span>
            </>
          )}
          {userApplication.status === "rejected" && (
            <>
              <FiXCircle className="cbx-cp__status-icon" />
              <span>Your application was not accepted</span>
            </>
          )}
          {userApplication.reviewNote && (
            <p className="cbx-cp__status-note">Note: {userApplication.reviewNote}</p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="cbx-cp__actions">
        {canApply && (
          <button
            className="cbx-cp__btn cbx-cp__btn--primary"
            onClick={() => setShowApplyModal(true)}
          >
            <FiSend /> Apply Now
          </button>
        )}

        {isAuthor && (
          <>
            <button
              className="cbx-cp__btn cbx-cp__btn--secondary"
              onClick={() => setShowManageModal(true)}
            >
              <FiSettings /> Manage Applications
            </button>
            {collab.isOpen && (
              <button
                className="cbx-cp__btn cbx-cp__btn--danger"
                onClick={() => setShowCloseModal(true)}
              >
                <FiX /> Close Collaboration
              </button>
            )}
          </>
        )}

        {!canApply && !isAuthor && !userApplication && (
          <div className="cbx-cp__message">
            {!user && <span>Sign in to apply</span>}
            {user && !collab.isOpen && <span>This collaboration is closed</span>}
            {user && collab.isOpen && isPastDeadline && <span>Application deadline has passed</span>}
            {user && collab.isOpen && !isPastDeadline && availablePositions === 0 && <span>All positions are filled</span>}
          </div>
        )}
      </div>

      {/* Modals */}
      <ApplyModal
        isOpen={showApplyModal}
        onClose={() => setShowApplyModal(false)}
        onSubmit={handleApply}
        positions={collab.positions}
      />

      <ManageApplicationsModal
        isOpen={showManageModal}
        onClose={() => setShowManageModal(false)}
        postId={post._id}
        onUpdate={onUpdate}
      />

      <ConfirmModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={handleClose}
        title="Close Collaboration?"
        message="This will prevent new applications. Existing applications can still be reviewed. This action cannot be undone."
        confirmText="Close Collaboration"
        cancelText="Cancel"
        type="warning"
        loading={closing}
      />
    </div>
  );
};

export default React.memo(CollaborationPost);
